import * as Sentry from "@sentry/react";
import Cookies from "js-cookie";

import { APP_ID } from "@/constants/appIdentifier";

type Options = {
  body?: Record<string, unknown>;
  params?: Record<string, string>;
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  baseUrl: string;
  signal?: AbortSignal;
};

async function fetchJSON<T>(_path: string, options?: Options) {
  const method = options?.method ?? "GET";
  const token = Cookies.get("token");
  let baseUrl = options?.baseUrl;

  if (baseUrl && baseUrl.endsWith("/")) {
    baseUrl = baseUrl.slice(0, -1);
  }

  let path = _path;
  if (_path.startsWith("/")) {
    path = _path.slice(1);
  }

  const url = `${baseUrl}/${path}`;

  const params = options?.params
    ? new URLSearchParams(options?.params)
    : undefined;

  const response = await fetch(
    `${url}${params ? `?${params.toString()}` : ""}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        "X-Requested-From": APP_ID,
      },
      method: method,
      body: options?.body ? JSON.stringify(options.body) : undefined,
    },
  );

  if (response.ok) {
    const { data }: { data: T } = await response.json();

    return data;
  } else {
    const json: { error_code?: string } = await response.json();

    if (response.status > 401) {
      Sentry.captureException(new Error("API Error"), {
        extra: {
          url: url,
          response: json,
        },
      });
    }

    if (json.error_code) {
      throw new Error(json.error_code);
    }

    throw new Error();
  }
}

export function fetchJSONFactory(baseUrl: string) {
  return <T>(path: string, options?: Omit<Options, "baseUrl">) => {
    const o = options ? options : {};
    return fetchJSON<T>(path, { ...o, baseUrl });
  };
}
